import React, { useState, useEffect, useMemo } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../AppContext';

// Game Components
import AuraEater from '../components/DreamWeave/games/aura-eater/AuraEater';
import CosmicAscent from '../components/DreamWeave/CosmicAscent';
import StarlightSentinel from '../components/DreamWeave/games/starlight-sentinel/StarlightSentinel';
import SynapticSerpent from '../components/DreamWeave/games/synaptic-serpent/SynapticSerpent';
import SynapticStacking from '../components/DreamWeave/SynapticStacking';

// A helper hook to get URL query parameters without external libraries
const useUrlQuery = () => {
    return useMemo(() => new URLSearchParams(window.location.search), []);
};

const GAME_COMPONENTS = {
    'aura_eater': AuraEater,
    'cosmic_ascent': CosmicAscent,
    'starlight_sentinel': StarlightSentinel,
    'synaptic_serpent': SynapticSerpent,
    'synaptic_stacking': SynapticStacking,
};

const AISpectatorPage = () => {
    const { t } = useTranslation();
    const { db, appId } = useAppContext();
    const query = useUrlQuery();
    const dreamWeaveId = query.get('dreamWeaveId');

    const [dreamWeave, setDreamWeave] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!dreamWeaveId || !db) {
            setError(t('aiSpectator.noIdError'));
            setIsLoading(false);
            return;
        }

        const dreamWeaveRef = doc(db, `artifacts/${appId}/public/data/dreamWeaves`, dreamWeaveId);
        
        const unsubscribe = onSnapshot(dreamWeaveRef, (docSnap) => {
            if (docSnap.exists()) {
                setDreamWeave({ id: docSnap.id, ...docSnap.data() });
            } else {
                setError(t('aiSpectator.notFoundError'));
            }
            setIsLoading(false);
        }, (err) => {
            console.error("Error fetching DreamWeave with onSnapshot:", err);
            setError(t('aiSpectator.loadError'));
            setIsLoading(false);
        });

        // Cleanup the listener when the component unmounts
        return () => unsubscribe();

    }, [dreamWeaveId, db, appId, t]);

    const GameComponent = dreamWeave ? GAME_COMPONENTS[dreamWeave.game] : null;

    if (isLoading) {
        return <div className="text-center p-8"><p className="text-white text-xl">{t('aiSpectator.loading')}</p></div>;
    }

    if (error) {
        return <div className="text-center p-8"><p className="text-red-400 text-xl">{error}</p></div>;
    }

    if (!dreamWeave || !GameComponent) {
        return <div className="text-center p-8"><p className="text-yellow-400 text-xl">{t('aiSpectator.noValidGame')}</p></div>;
    }

    return (
        <div className="w-full h-full flex flex-col items-center justify-center bg-black bg-opacity-50 rounded-lg p-4">
            <h1 className="text-3xl font-bold text-blue-300 mb-4">{t('aiSpectator.title', { title: dreamWeave.title })}</h1>
            <p className="text-white mb-4">{t('aiSpectator.watchingLabel')} <span className="font-bold">{dreamWeave.game}</span> | <span className="font-bold text-purple-300">{t('aiSpectator.soulsLabel')}: {dreamWeave.souls || 0}</span></p>
            <div className="w-full max-w-2xl aspect-video bg-gray-900 rounded-lg overflow-hidden">
                <GameComponent 
                    dreamWeaveId={dreamWeaveId} 
                    souls={dreamWeave.souls} 
                    isSpectatorMode={true} 
                />
            </div>
        </div>
    );
};

export default AISpectatorPage;
